import { Link } from "react-router-dom";
import {
  AlertCircle,
  ShieldCheck,
  Eye,
  ClipboardCheck,
  ArrowRight,
  FileSearch,
  Droplets,
  Zap,
  Flame,
  Layers,
  Home as HomeIcon,
} from "lucide-react";
import SEO from "@/components/SEO";

const URGENT_TYPES = [
  { i: <Layers />, t: "Roof failure or major leak", d: "Visible damage, water ingress, ceiling movement or active dripping after weather." },
  { i: <Droplets />, t: "Water ingress", d: "Damp patches, swelling timber, soft floors or water tracking through cavities." },
  { i: <HomeIcon />, t: "Structural movement or instability", d: "Cracks, doors sticking, floor tilt, base shift or settlement concerns." },
  { i: <Zap />, t: "Electrical issues or faults", d: "Tripping, scorching, smell of burning, intermittent failures or unsafe wiring." },
  { i: <Flame />, t: "Heating or boiler failure", d: "No heat, no hot water, suspected leak, fault codes or carbon monoxide concern." },
];

const RISK_BULLETS = [
  "The issue is misdiagnosed",
  "The wrong contractor is used",
  "Work is over-specified",
  "Costs are inflated due to urgency",
  "Insurance relevance is missed",
];

const HOW_WE_HELP = [
  { i: <Eye />, t: "Immediate assessment", d: "We take a proper look at what's happening — not panic-driven, not contractor-driven." },
  { i: <ClipboardCheck />, t: "Guidance before any work is approved", d: "Pause the situation long enough to make the right decision, not the fastest one." },
  { i: <FileSearch />, t: "Review of quotes or proposed repairs", d: "If a contractor has already quoted, we'll tell you whether the scope and cost stack up." },
  { i: <ShieldCheck />, t: "Advice on whether inspection is required", d: "Some issues need a proper independent inspection before work — we'll tell you when." },
  { i: <AlertCircle />, t: "Coordination of next steps", d: "We help you sequence what to do, in what order, with whom — without the rush tax." },
];

export default function UrgentRepairs() {
  return (
    <div data-testid="urgent-repairs-page">
      <SEO
        title="Park Home Urgent Repairs | What To Do Before Acting"
        description="Urgent park home issue? Understand what to do before calling a contractor. Avoid overpaying and getting it wrong."
        keywords="park home urgent repairs, park home emergency, park home roof leak, park home water ingress, park home electrical fault"
        canonical="https://parkhomesupport.co.uk/park-home-urgent-repairs"
        faq={[
          { q: "What counts as an urgent park home repair?",
            a: "Roof failure or major leak, water ingress, structural movement or instability, electrical issues or faults, and heating or boiler failure are the most common urgent situations. These are the cases where the right decision in the next 24–72 hours saves the most money." },
          { q: "Should I just call a contractor straight away?",
            a: "Not before getting independent guidance. Many urgent repairs go wrong because pressure replaces process — issues get misdiagnosed, work gets over-specified, costs get inflated and insurance relevance gets missed. A short pause for the right advice usually saves thousands." },
          { q: "Could my urgent issue be an insurance claim?",
            a: "Possibly. Some urgent issues — storm damage, escape of water, impact damage — are insurable events. Acting too quickly (replacing materials, paying contractors out of pocket) can void a valid claim before anyone has even looked at it." },
          { q: "How quickly can Park Home Support respond?",
            a: "We provide immediate assessment of the situation and guidance before any work is approved. We're not a call-out service — we're independent guidance that ensures the right action is taken before you commit money or sign anything off." },
        ]}
      />

      {/* HERO — calm authority, not panic */}
      <section className="relative hero-gradient overflow-hidden">
        <div className="container-phs py-16 lg:py-24">
          <div className="max-w-4xl">
            <span className="eyebrow">Urgent Support</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#071A33]">
              Park Home Urgent Repairs — <span className="text-[#064E2E]">What To Do First.</span>
            </h1>
            <p className="mt-6 text-lg text-[#334155] max-w-2xl leading-relaxed">
              When something fails, acting quickly matters — but acting without the right guidance can lead to unnecessary cost or incorrect repairs.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote-review" className="btn-primary" data-testid="urgent-hero-cta">
                Request Urgent Support <ArrowRight size={18} />
              </Link>
              <Link to="/request-support" className="btn-outline" data-testid="urgent-hero-secondary">
                Describe the Situation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT COUNTS AS URGENT */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">When It's Urgent</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">
              What counts as urgent on a park home.
            </h2>
            <p className="mt-4 text-[#334155] text-lg">
              These are the situations where the right decision in the next 24–72 hours will save you the most money.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {URGENT_TYPES.map((u) => (
              <div key={u.t} className="card-soft" data-testid={`urgent-type-${u.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center mb-4">
                  {u.i}
                </div>
                <h3 className="font-display text-lg text-[#071A33] leading-snug">{u.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RISK OF ACTING TOO QUICKLY */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">The Hidden Cost of Rushing</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">
                The risk of acting too quickly.
              </h2>
              <p className="mt-4 text-[#334155] text-lg leading-relaxed">
                Many urgent repairs go wrong because pressure replaces process. The same problem, handled calmly with the right guidance, often costs a fraction of the panic version.
              </p>
              <p className="mt-6 text-[#064E2E] font-semibold text-lg leading-snug">
                Urgent does not mean rushed — getting it wrong quickly is still getting it wrong.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="card-soft border-l-4 border-[#B88A2A]">
                <p className="eyebrow text-[#B88A2A] mb-4">What goes wrong</p>
                <ul className="space-y-3">
                  {RISK_BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#071A33]">
                      <span className="mt-2 w-1.5 h-1.5 bg-[#B88A2A] rounded-full shrink-0" />
                      <span className="text-base leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">Controlled Response</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">
              How we help you handle it properly.
            </h2>
            <p className="mt-4 text-[#334155] text-lg">
              We're not a call-out service. We're independent guidance that ensures the right action is taken before you commit money or sign anything off.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {HOW_WE_HELP.map((h) => (
              <div key={h.t} className="card-default">
                <div className="w-11 h-11 bg-[#0F766E] text-white rounded-xl flex items-center justify-center mb-4">
                  {h.i}
                </div>
                <h3 className="font-display text-lg text-[#071A33] leading-snug">{h.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSURANCE CONSIDERATIONS */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow">Insurance Considerations</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">
                Some urgent issues are insurable events.
              </h2>
              <p className="mt-4 text-[#334155] text-lg leading-relaxed">
                Acting too quickly — replacing materials, removing damage, paying contractors out of pocket — can quietly destroy a valid claim before anyone has even looked at it.
              </p>
              <ul className="mt-6 space-y-3 text-[#071A33]">
                {[
                  "Void potential claims by removing evidence",
                  "Lead to unnecessary out-of-pocket costs",
                  "Reduce or eliminate insurer payout entitlement",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 bg-[#064E2E] rounded-full shrink-0" />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/insurance" className="btn-outline mt-7 inline-flex" data-testid="urgent-insurance-cta">
                Request Insurance Guidance <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="card-teal">
                <p className="text-white/80 text-sm uppercase tracking-[0.22em] font-medium">Important</p>
                <p className="font-display text-2xl mt-3 leading-snug">
                  Speak to us before approving any urgent work — even a 30-minute pause can preserve thousands in cover.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIMARY CTA */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="rounded-3xl bg-white border border-[#D7E7DF] p-8 md:p-12 shadow-sm">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl sm:text-4xl text-[#071A33] leading-tight">
                Need help with an urgent issue?
              </h2>
              <p className="mt-4 text-[#475569] text-lg leading-relaxed">
                We'll take a look and guide you on the right next step before you commit to any work.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/quote-review" className="btn-primary" data-testid="urgent-final-cta">
                  Request Urgent Support <ArrowRight size={18} />
                </Link>
                <Link to="/membership" className="btn-outline" data-testid="urgent-final-membership">
                  Join for £99 First Year
                </Link>
              </div>
              <p className="text-xs text-[#94A3B8] mt-6">
                This is independent technical guidance — not insurance, not a warranty, and not a contractor service.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
